'use client';

import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { HelpCircle } from 'lucide-react';
import { helpCommandDescription } from '../../../packages/plugin-shared/help.mjs';
import { keybindDefault, resolveChatKeybinds, validateChatKeybinds, type WebKeybindAction } from '../../../packages/plugin-shared/chatKeys.mjs';
import { useBrainChat } from './BrainChatProvider';
import { useChatKeybindings } from './useChatKeybindings';
import { useMyTerminalSettings } from '../../lib/queries';
import { useSaveMyTerminalSettings } from '../../lib/mutations';
import { useRevisionHold } from '../account/useRevisionHold';
import { rereadQuery } from '../../lib/rereadQuery';
import { AUTOSAVE_UNCHANGED, closeAfterFlush, useAutoSaveStatus } from '../../lib/useAutoSaveStatus';
import { useTranslation } from '../../lib/i18n';
import { useAutosaveToast } from '../../components/ui/Toast';
import { RegisterSearch } from '../../components/ui/RegisterSearch';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/states';
import { Modal, ModalBody } from '../../components/ui/Modal';
import { Segmented } from '../../components/ui/Segmented';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { ActionMenu } from '../../components/ui/ActionMenu';
import { SettingsList, SettingsItem } from '../../components/ui/SettingsSurface';
import { SettingsPresentationContext } from '../../components/ui/settingsPresentation';
import { HelpTip } from '../../components/ui/HelpTip';
import { FieldError } from '../../components/ui/shadcn/field';

type Overrides = Partial<Record<WebKeybindAction, string>>;

export function HelpModal({ onClose, initialTab = 'commands' }: { onClose: () => void; initialTab?: 'commands' | 'shortcuts' }) {
  const { t, locale } = useTranslation();
  const { commands, runSlash } = useBrainChat();
  const shortcuts = useChatKeybindings();
  const query = useMyTerminalSettings();
  const save = useSaveMyTerminalSettings();
  const [draft, setDraft] = useState<Overrides | null>(null);
  const [action, setAction] = useState<WebKeybindAction | null>(null);
  const [issueSpec, setIssueSpec] = useState<string | null>(null);
  const [leaderPending, setLeaderPending] = useState(false);
  const accepted = useRef('');
  const owner = useId();
  const [filter, setFilter] = useState('');
  const [shortcutFilter, setShortcutFilter] = useState('');
  const [tab, setTab] = useState<string>(initialTab);

  useEffect(() => {
    if (draft === null && shortcuts.effective) {
      const initial = shortcuts.effective.canonicalOverrides;
      accepted.current = JSON.stringify(initial);
      setDraft(initial);
    }
  }, [draft, shortcuts.effective]);

  const reload = async () => {
    const fresh = await rereadQuery(query.refetch);
    const result = validateChatKeybinds(fresh.chatKeybinds, { surface: 'web' });
    if (!result.ok) throw new Error('Invalid stored web keymap');
    accepted.current = JSON.stringify(result.value);
    setDraft(result.value);
    setAction(null);
    setIssueSpec(null);
    autosave.reset();
  };
  const write = useRevisionHold({ reload });
  const serialized = JSON.stringify(draft);
  const autosave = useAutoSaveStatus([serialized], async () => {
    if (serialized === accepted.current || !draft) return AUTOSAVE_UNCHANGED;
    await write(() => save.mutateAsync({ chatKeybinds: draft }));
    accepted.current = serialized;
  }, { ready: draft !== null, reload, classify: () => ({ kind: 'operation', message: t.brainChat.ux.shortcutSaveError }) });
  useAutosaveToast(autosave, { resource: owner });

  const preview = useMemo(() => draft ? resolveChatKeybinds(draft, { surface: 'web' }) : null, [draft]);
  const proposed = action && draft && issueSpec !== null ? validateChatKeybinds({ ...draft, [action]: issueSpec }, { surface: 'web' }) : null;
  const issue = proposed && !proposed.ok ? proposed.issues[0] : null;
  const issueText = issue?.code === 'collision'
    ? t.brainChat.ux.shortcutConflict.replace('{action}', t.brainChat.ux.shortcutActions[(issue.owner === action ? issue.action : issue.owner) as WebKeybindAction])
    : issue?.code === 'reserved' ? t.brainChat.ux.shortcutReserved : t.brainChat.ux.shortcutInvalid;

  const change = (key: WebKeybindAction, value: string | null) => {
    if (!draft) return;
    const next = { ...draft };
    if (value === null) delete next[key]; else next[key] = value;
    const checked = validateChatKeybinds(next, { surface: 'web' });
    if (checked.ok) {
      setDraft(checked.value);
      setAction(null);
      setIssueSpec(null);
    } else {
      setAction(key);
      setIssueSpec(value ?? keybindDefault(key));
    }
  };
  const startEdit = (key: WebKeybindAction) => {
    setAction(key);
    setIssueSpec(null);
    setLeaderPending(false);
  };
  const captureKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.repeat || ['Control', 'Shift', 'Alt', 'Meta'].includes(event.key)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setAction(null);
      setIssueSpec(null);
      setLeaderPending(false);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const base = ({ ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', ' ': 'space' } as Record<string, string>)[event.key] ?? event.key.toLowerCase();
    const chord = [event.ctrlKey && 'ctrl', event.shiftKey && 'shift', event.altKey && 'alt', event.metaKey && 'super', base].filter(Boolean).join('+');
    if (!leaderPending && action !== 'leader' && preview?.bindings.get('leader')?.some(b => b.chord.id === chord)) {
      setLeaderPending(true);
      return;
    }
    if (action) change(action, `${leaderPending ? 'leader ' : ''}${chord}`);
    setLeaderPending(false);
  };
  const rows = useMemo(() => {
    const needle = filter.trim().replace(/^\//, '').toLowerCase();
    const all = commands.filter((c) => c.name !== 'help').map(command => ({
      command,
      description: helpCommandDescription({ lang: locale, command, place: 'chat', placeLoc: locale === 'en' ? 'chat' : 'chatu' }),
    }));
    if (!needle) return all;
    return all.filter((c) => c.command.name.toLowerCase().includes(needle) || c.description.toLowerCase().includes(needle));
  }, [commands, filter, locale]);

  return (
    <Modal title={tab === 'commands' ? t.brainChat.ux.shortcutsCommands : t.brainChat.ux.shortcutsTitle} onClose={() => { if (action) { setAction(null); setIssueSpec(null); setLeaderPending(false); } else { void closeAfterFlush(onClose, autosave.flush); } }} size="md" icon={HelpCircle} reportedBy={owner}
      tabs={<Segmented variant="line" value={tab} onChange={setTab} options={[
        { value: 'shortcuts', label: t.brainChat.ux.shortcutsTitle },
        { value: 'commands', label: t.brainChat.ux.shortcutsCommands },
      ]} />}>
      <ModalBody gap={4}>
        {tab === 'commands' ? <>
          <div className="flex items-center gap-2">
            <RegisterSearch value={filter} onChange={setFilter} placeholder={t.helpModal.filterPlaceholder} />
            <HelpTip align="left">{t.brainChat.rewindHelp}</HelpTip>
          </div>
          {rows.length === 0 ? <EmptyState title={t.helpModal.emptyTitle} description={t.helpModal.emptyDesc} icon={HelpCircle} /> :
            <SettingsPresentationContext value="drawer">
              <SettingsList>
                {rows.map(({ command: cmd, description }) => <SettingsItem key={cmd.name}
                  title={<span className="font-mono text-small">{`/${cmd.name}`}</span>}
                  description={description}
                  onClick={() => { void closeAfterFlush(() => { onClose(); runSlash(cmd); }, autosave.flush); }} />)}
              </SettingsList>
            </SettingsPresentationContext>}
        </> : <>
          <div className="flex items-center gap-2">
            <RegisterSearch value={shortcutFilter} onChange={setShortcutFilter} placeholder={t.helpModal.shortcutFilterPlaceholder} />
            <HelpTip align="left">{t.brainChat.ux.shortcutsScope}</HelpTip>
          </div>
          {shortcuts.error || query.isError ? <ErrorState message={t.common.daemonUnreachable} onRetry={() => { void query.refetch(); }} /> :
            !draft || !preview ? <LoadingState /> :
            <SettingsPresentationContext value="drawer">
              <SettingsList>
                {shortcuts.rows.filter(({ action: key }) => t.brainChat.ux.shortcutActions[key].toLowerCase().includes(shortcutFilter.trim().toLowerCase())).map(({ action: key }) => {
                  const bindings = preview.bindings.get(key) ?? [];
                  const editing = action === key;
                  return <Fragment key={key}>
                    <SettingsItem
                      title={t.brainChat.ux.shortcutActions[key]}
                      actions={<>
                        {editing ? <Input className="w-36" autoFocus readOnly aria-label={t.brainChat.ux.shortcutCapture} aria-invalid={!!issue} aria-describedby={issue ? `${owner}-${key}-error` : undefined} value={leaderPending ? 'leader …' : ''} placeholder={t.helpModal.shortcutCapturePlaceholder} onKeyDown={captureKey} /> :
                          <Button type="button" variant="ghost" onClick={() => startEdit(key)} aria-label={`${t.brainChat.ux.shortcutChange}: ${t.brainChat.ux.shortcutActions[key]}`}>
                            {bindings.length ? bindings.map((b, i) => <Badge key={i} tone="muted">{b.leader ? 'leader ' : ''}{b.chord.id}</Badge>) : t.brainChat.ux.shortcutUnbound}
                          </Button>}
                        {!editing ? <ActionMenu variant="kebab" label={t.brainChat.ux.shortcutActions[key]} items={[
                          { action: 'edit', onSelect: () => {}, onAfterClose: () => startEdit(key) },
                          ...(bindings.length ? [{ action: 'disable' as const, onSelect: () => change(key, 'none') }] : []),
                          ...(preview.custom.has(key) ? [{ action: 'reset' as const, onSelect: () => change(key, null) }] : []),
                        ]} /> : null}
                      </>} />
                    {editing && issue ? <FieldError id={`${owner}-${key}-error`} className="px-4">{issueText}</FieldError> : null}
                  </Fragment>;
                })}
              </SettingsList>
            </SettingsPresentationContext>}
        </>}
      </ModalBody>
    </Modal>
  );
}
